import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Heart, Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@harmonyhouse.org");
  const [password, setPassword] = useState("Harmony2026!");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-forest text-white p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1732783384071-0cdb7bbbad94?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
            <Heart className="h-6 w-6 text-white" fill="currentColor" />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold">David's Harmony House</div>
            <div className="text-xs tracking-widest text-forest-muted uppercase">Foundation</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-5xl font-semibold leading-tight">
            Integrated Financial<br />Management System
          </h1>
          <p className="text-forest-muted mt-4 max-w-md">
            Steward every dollar with confidence — double-entry accounting, fund tracking, grants,
            donors and board-ready reporting in one trusted platform.
          </p>
        </div>
        <div className="relative text-xs text-forest-muted">Version 1.0 · Enterprise Nonprofit Suite</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-8 bg-bone">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <h2 className="font-heading text-3xl font-semibold text-[#1A1A1A]">Sign in</h2>
          <p className="text-sm text-[#5C5C54] mt-1.5 mb-8">Access your financial command center.</p>

          <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            data-testid="login-email"
            className="w-full bg-white border border-stone-300 rounded-md px-3.5 py-2.5 text-sm mb-4 outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
          />
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">Password</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            data-testid="login-password"
            className="w-full bg-white border border-stone-300 rounded-md px-3.5 py-2.5 text-sm mb-6 outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
          />
          <button
            type="submit" disabled={busy} data-testid="login-submit"
            className="w-full bg-forest hover:bg-forest-900 text-white rounded-md px-4 py-2.5 text-sm font-medium transition-colors duration-200 flex items-center justify-center disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Sign in
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-stone-200" />
            <span className="text-xs text-[#8A9F95] uppercase tracking-wider">or</span>
            <div className="h-px flex-1 bg-stone-200" />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            type="button" data-testid="google-login-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + "/";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-white border border-stone-300 hover:bg-stone-50 text-stone-800 rounded-md px-4 py-2.5 text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2.5"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
            Continue with Google
          </button>

          <div className="mt-6 text-xs text-[#5C5C54] bg-stoney border border-stone-200 rounded-md p-3">
            <div className="font-medium text-[#1A1A1A] mb-1">Demo credentials</div>
            Admin · admin@harmonyhouse.org / Harmony2026!<br />
            Finance · finance@harmonyhouse.org / Finance2026!
          </div>
        </form>
      </div>
    </div>
  );
}
